/**
 * Checks if a field is marked as dirty in the React Hook Form dirtyFields object.
 *
 * The dirtyFields object uses nested structure matching the form data, where:
 * - A value of `true` means the field or all its children are dirty
 * - A nested object means only specific children are dirty
 *
 * @param fieldPath - Dot-notation path to the field (e.g., "address.postalcode")
 * @param dirtyFields - The dirtyFields object from React Hook Form's formState
 * @returns true if the field has been modified by the user
 *
 * @example
 * // For dirtyFields = { address: { postalcode: true } }
 * isFieldDirty('address.postalcode', dirtyFields) // true
 * isFieldDirty('address.city', dirtyFields) // false
 *
 * // For dirtyFields = { address: true }
 * isFieldDirty('address.postalcode', dirtyFields) // true (parent is dirty)
 */
export const isFieldDirty = (fieldPath: string, dirtyFields: Record<string, unknown>): boolean => {
  const parts = fieldPath.split('.');
  let current: unknown = dirtyFields;

  for (const part of parts) {
    // If we've hit a `true` at any parent level, the field is dirty
    if (current === true) return true;

    // If current is not an object or is null, the field is not dirty
    if (!current || typeof current !== 'object') return false;

    current = (current as Record<string, unknown>)[part];
  }

  // The final value is either true, or an object (meaning children are dirty, so this field counts as dirty)
  return current === true || (typeof current === 'object' && current !== null);
};
